import type { MetagameTokenPackageBundle } from '../../../dto/metagames.types';
import type {
  ExtraOfferBundlePayload,
  IOrderPackage,
  MysteryBundlePayload,
  PackageBundle,
} from '../../../stores/app/types';
import { getBingoCardsPackageBundles } from '../../../stores/bingo/helpers';
import { isCoinBackPackageBundles } from '../../../stores/coin-back/helpers';
import type { ChallengePayload, FreeSpinsPayload, MetaGameFeaturePayload } from '../../../stores/rewards/types';
import { SubscriptionUtils } from '../../../stores/subscription/utils';

import type { PaymentServicePurchasePackage } from './types';

export function mapStorePackageToPaymentPackage(
  storePkg: IOrderPackage,
  triggeredBy: string,
): PaymentServicePurchasePackage {
  const { packageId, price, gcAmount, scAmount, packageBundles } = storePkg;

  const allMetagamesTokenBundles = (packageBundles?.filter((bundle) => bundle.bundleType === 'metagameToken') ??
    []) as MetagameTokenPackageBundle[];
  const bingoCards = getBingoCardsPackageBundles(allMetagamesTokenBundles);

  const metagamesTokenBundles = bingoCards?.length
    ? allMetagamesTokenBundles.filter((bundle) => !bingoCards.includes(bundle))
    : allMetagamesTokenBundles;

  const freeSpinsBundles =
    packageBundles?.filter(
      (bundle): bundle is PackageBundle & { bundlePayload: FreeSpinsPayload } =>
        bundle.bundleType === 'freeSpins' || bundle.bundleType === 'minigameRound',
    ) ?? [];
  const challengesBundles =
    packageBundles?.filter(
      (bundle): bundle is PackageBundle & { bundlePayload: ChallengePayload } => bundle.bundleType === 'challenge',
    ) ?? [];
  const metagameFeatureBundles =
    packageBundles?.filter(
      (bundle): bundle is PackageBundle & { bundlePayload: MetaGameFeaturePayload } =>
        bundle.bundleType === 'metagameFeature',
    ) ?? [];
  const extraOfferBundles =
    packageBundles?.filter(
      (bundle): bundle is PackageBundle & { bundlePayload: ExtraOfferBundlePayload } =>
        bundle.bundleType === 'extraOffer',
    ) ?? [];
  const mysteryBundles =
    packageBundles?.filter(
      (bundle): bundle is PackageBundle & { bundlePayload: MysteryBundlePayload } => bundle.bundleType === 'mystery',
    ) ?? [];

  return {
    packageId: Number(packageId),
    price: Number(price),
    gcCoin: Number(gcAmount),
    scCoin: Number(scAmount),
    triggered_by: triggeredBy,
    ...(!!bingoCards?.length && {
      ccCards: SubscriptionUtils.getCardsCounts(bingoCards, 'GC'),
      sweepsCards: SubscriptionUtils.getCardsCounts(bingoCards, 'CASH'),
      ccCurrencySymbol: SubscriptionUtils.getCurrencySymbol(bingoCards, 'GC'),
      sweepsCurrencySymbol: SubscriptionUtils.getCurrencySymbol(bingoCards, 'CASH'),
    }),
    ...(packageBundles && isCoinBackPackageBundles(packageBundles)
      ? {
          coinBack: packageBundles.find((bundle) => bundle.bundleType === 'coinBack')?.bundlePayload,
        }
      : {}),
    ...(metagamesTokenBundles.length > 0
      ? {
          metagamesTokensBundles: metagamesTokenBundles.map((bundle) => ({
            id: bundle.id,
            amount: Number(bundle.bundlePayload.amount),
            currencySymbol: bundle.bundlePayload.currencySymbol,
            coinType: bundle.bundlePayload.coinType,
          })),
        }
      : {}),
    ...(freeSpinsBundles.length > 0
      ? {
          freeSpinsBundles: freeSpinsBundles.map((bundle) => ({
            id: bundle.id,
            gameImageUrl: bundle.bundlePayload.gameImageUrl,
            rounds: bundle.bundlePayload.rounds,
            coinType: bundle.bundlePayload.coinType,
            bet: bundle.bundlePayload.bet,
          })),
        }
      : {}),
    ...(challengesBundles.length > 0
      ? {
          challengesBundles: challengesBundles.map((bundle) => ({
            symbolUrl: bundle.bundlePayload.symbolUrl,
          })),
        }
      : {}),
    ...(metagameFeatureBundles.length > 0
      ? {
          metagameFeatureBundles: metagameFeatureBundles.map((bundle) => ({
            amount: bundle.bundlePayload.amount,
            symbolUrl: bundle.bundlePayload.symbolUrl,
            featureType: bundle.bundlePayload.featureType,
            metagameTypeName: bundle.bundlePayload.metagameTypeName,
          })),
        }
      : {}),
    ...(extraOfferBundles.length > 0
      ? {
          extraOfferBundles: extraOfferBundles.map((bundle) => ({
            id: bundle.id,
            extraPercentage: bundle.bundlePayload.extraPercentage,
            rewardImageUrl: bundle.bundlePayload.rewardImageUrl,
          })),
        }
      : {}),
    // Only the icon: the order summary must never see the pool (options, odds, prizes).
    ...(mysteryBundles.length > 0
      ? {
          mysteryBundles: mysteryBundles.map((bundle) => ({
            id: bundle.id,
            imageUrl: bundle.bundlePayload.visuals.mysteryIcon,
          })),
        }
      : {}),
  };
}
